import { Box, Typography, useTheme } from "@mui/material";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderTemplateChips } from "web_myplann/Reminders/Components/ReminderTemplateChips";
import type { ReminderFormModel } from "web_myplann/Reminders/Forms/reminderFormSchema";
import { useReminderRecurrenceOptions } from "web_myplann/Reminders/Hooks/useReminderRecurrenceOptions";
import { useReminderTemplateSelection } from "web_myplann/Reminders/Hooks/useReminderTemplateSelection";
import type {
	Reminder,
	ReminderTemplate,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { formatReminderDate } from "web_myplann/Reminders/Utils/formatReminderDate";
import { getReminderMinDate } from "web_myplann/Reminders/Utils/reminderMinDate";
import { FlatRowDateDayPanel } from "web_myplann/Shared/Components/FlatRow/FlatRowDateDayPanel";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowGroup } from "web_myplann/Shared/Components/FlatRow/FlatRowGroup";
import { ReminderDateCalendar } from "web_myplann/Shared/Components/ReminderDateCalendar";
import { ReminderDuplicateAlert } from "web_myplann/Shared/Components/ReminderDuplicateAlert";

type Props = {
	form: UseFormReturn<ReminderFormModel>;
	templates: ReminderTemplate[];
	duplicate: Reminder | null;
	onOpenDuplicate: (reminder: Reminder) => void;
	duplicateAlertId: string;
};

export const ReminderCreateFields = ({
	form,
	templates,
	duplicate,
	onOpenDuplicate,
	duplicateAlertId,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const hasGoalsRight = useGoalsRightOptional();
	const { control, watch } = form;
	const { selectTemplate, markAsCustom } = useReminderTemplateSelection(
		form,
		templates,
	);

	const minDate = getReminderMinDate();
	const recurrenceOptions = useReminderRecurrenceOptions();

	return (
		<>
			<FlatRowGroup>
				<ReminderTemplateChips
					templates={templates}
					selectedTypeKey={watch("typeKey")}
					onSelect={selectTemplate}
				/>

				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Reminders.Form.nameLabel)}
					placeholder={t(Resources.Reminders.Form.namePlaceholder)}
					onValueChange={markAsCustom}
					isLast
				/>
			</FlatRowGroup>
			<Box
				mt={theme.spacingTokens.md}
				bgcolor={theme.palette.background.paper}
				borderTop={theme.colors.border.solid(theme.palette.surface.border)}
				borderBottom={theme.colors.border.solid(theme.palette.surface.border)}
			>
				<Controller
					control={control}
					name="date"
					render={({ field: { value, onChange, onBlur } }) => (
						<>
							{hasGoalsRight && (
								<ReminderDateCalendar
									value={value instanceof Date ? value : null}
									minDate={minDate}
									onSelect={(date) => {
										onChange(date);
										onBlur();
									}}
								>
									{duplicate && (
										<ReminderDuplicateAlert
											id={duplicateAlertId}
											duplicate={duplicate}
											onOpen={onOpenDuplicate}
										/>
									)}
								</ReminderDateCalendar>
							)}
							{!hasGoalsRight && (
								<Box
									px={theme.spacingTokens.lg}
									pt={theme.spacingTokens.md}
									pb={theme.spacingTokens.sm}
									borderBottom={theme.colors.border.solid(
										theme.palette.surface.border,
									)}
								>
									<Typography
										variant="caption"
										component="div"
										color={theme.palette.text.secondary}
										mb={theme.spacingTokens.sm}
									>
										{value
											? t(Resources.Reminders.Form.selectedDate, {
													date: formatReminderDate(value),
												})
											: t(Resources.Reminders.Form.selectDate)}
									</Typography>

									{duplicate && (
										<ReminderDuplicateAlert
											id={duplicateAlertId}
											duplicate={duplicate}
											onOpen={onOpenDuplicate}
										/>
									)}

									<FlatRowDateDayPanel
										value={value instanceof Date ? value : null}
										minDate={minDate}
										onSelect={(date) => {
											onChange(date);
											onBlur();
										}}
									/>
								</Box>
							)}
						</>
					)}
				/>

				<FlatRowFormSelect
					control={control}
					name="recurrence"
					label={t(Resources.Reminders.Form.recurrenceLabel)}
					options={recurrenceOptions}
					drawerTitle={t(Resources.Reminders.Form.recurrenceLabel)}
					isLast
					onValueChange={markAsCustom}
				/>
			</Box>
		</>
	);
};
